import { Component, Input, OnInit } from '@angular/core';
import { Modal } from '../modal';
import { ModalHeaderComponent } from '../components/modal-header/modal-header.component';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { Operation } from '../../enums/utils/operations.enum';
import { InputTextareaSubtleComponent } from '../../components/subtle-inputs/input-textarea-subtle/input-textarea-subtle.component';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ILocationGroup } from '../../models/location-group/location.group.model';
import { LocationGroupsService } from '../../services/entities/location-groups/location-groups.service';
import { SelectedOrgService } from '../../services/selected-org.service';
import { take } from 'rxjs';
import { UtilsService } from '../../services/utils.service';
import { CheckboxModule } from 'primeng/checkbox';

interface IForm {
  title: FormControl<string>;
  external: FormControl<boolean>;
  shortDescription: FormControl<string | null>;
}

@Component({
  selector: 'app-location-group-modal',
  standalone: true,
  imports: [
    ModalHeaderComponent, TranslateModule, ReactiveFormsModule,
    ButtonModule, CheckboxModule,
    InputTextareaSubtleComponent
  ],
  templateUrl: './location-group-modal.component.html',
  styleUrl: './location-group-modal.component.scss'
})
export class LocationGroupModalComponent extends Modal implements OnInit {
  Operation = Operation;

  @Input({required: true}) operation: Operation | undefined;
  @Input({required: true}) locationGroup: ILocationGroup | null | undefined;
  @Input() onChange?: (locationGroupId: number) => void;

  form = new FormGroup<IForm>({
    title: new FormControl<string>('', { nonNullable: true, validators: [ Validators.required ]}),
    external: new FormControl<boolean>(false, { nonNullable: true }),
    shortDescription: new FormControl<string | null>(null),
  });

  saving: boolean = false;

  constructor(
    private locationGroupsService: LocationGroupsService,
    private selectedOrg: SelectedOrgService,
    private utils: UtilsService
  ) {
    super();
  }

  override ngOnInit(): void {
    super.ngOnInit();

    if (this.operation === Operation.UPDATE && this.locationGroup) {
      this.form.patchValue({
        title: this.locationGroup.title,
        external: this.locationGroup.external,
        shortDescription: this.locationGroup.shortDescription
      });
    } else if (this.operation === Operation.CREATE) {
      this.form.reset();
    }
  }

  onSubmit() {
    const orgId = this.selectedOrg.getCurrentValue();
    if (!orgId || this.saving) return;
    this.utils.markFormGroupDirty(this.form);
    if (this.form.invalid) {
      console.error('Form is invalid');
      return;
    }

    if (this.operation === Operation.UPDATE) {
      if (!this.locationGroup) {
        console.error('Location group is not defined for update operation');
        return;
      }
      this.saving = true;
  
      const formVals = this.form.getRawValue();
      this.locationGroupsService.update({
        id: this.locationGroup.id,
        title: formVals.title,
        external: formVals.external,
        shortDescription: formVals.shortDescription,
      }).pipe(
        take(1)
      ).subscribe({
        next: (locationGroup) => {
          this.saving = false;
          if (this.onChange) this.onChange(locationGroup.id);
          this.close();
        },
        error: (err) => {
          console.error('Error updating location group:', err);
          this.saving = false;
        }
      })
    }
    if (this.operation === Operation.CREATE) {
      this.saving = true;
  
      const formVals = this.form.getRawValue();
      this.locationGroupsService.create({
        title: formVals.title,
        external: formVals.external,
        shortDescription: formVals.shortDescription,
        orgId
      }).pipe(
        take(1)
      ).subscribe({
        next: (locationGroup) => {
          this.saving = false;
          if (this.onChange) this.onChange(locationGroup.id);
          this.close();
        },
        error: (err) => {
          console.error('Error creating location group:', err);
          this.saving = false;
        }
      })
    }
  }

}
